import {
  ApolloError,
  ApolloQueryResult,
  FetchMoreQueryOptions,
  NetworkStatus,
  SubscribeToMoreOptions,
  UpdateQueryOptions,
  DataProxy,
} from '@apollo/client';

export class UserError extends Error {}

export enum TranslatableResourceType {
  Branding = 'BRANDING',
  Collection = 'COLLECTION',
  DeliveryMethodDefinition = 'DELIVERY_METHOD_DEFINITION',
  DeliveryProfile = 'DELIVERY_PROFILE',
  EmailTemplate = 'EMAIL_TEMPLATE',
  Link = 'LINK',
  MediaImage = 'MEDIA_IMAGE',
  Video = 'VIDEO',
  Metafield = 'METAFIELD',
  OnlineStoreArticle = 'ONLINE_STORE_ARTICLE',
  OnlineStoreBlog = 'ONLINE_STORE_BLOG',
  OnlineStoreFilterSetting = 'ONLINE_STORE_FILTER_SETTING',
  OnlineStoreFilterSettingGroup = 'ONLINE_STORE_FILTER_SETTING_GROUP',
  OnlineStoreMenu = 'ONLINE_STORE_MENU',
  OnlineStorePage = 'ONLINE_STORE_PAGE',
  OnlineStoreThemeJsonTemplate = 'ONLINE_STORE_THEME_JSON_TEMPLATE',
  OnlineStoreThemeSectionGroup = 'ONLINE_STORE_THEME_SECTION_GROUP',
  OnlineStoreThemeSettingsCategory = 'ONLINE_STORE_THEME_SETTINGS_CATEGORY',
  OnlineStoreThemeLocaleContent = 'ONLINE_STORE_THEME_LOCALE_CONTENT',
  OnlineStoreThemeSettingsDataSections = 'ONLINE_STORE_THEME_SETTINGS_DATA_SECTIONS',
  OnlineStoreThemeAppEmbed = 'ONLINE_STORE_THEME_APP_EMBED',
  PackingSlipTemplate = 'PACKING_SLIP_TEMPLATE',
  PaymentGateway = 'PAYMENT_GATEWAY',
  Product = 'PRODUCT',
  ProductOption = 'PRODUCT_OPTION',
  ProductVariant = 'PRODUCT_VARIANT',
  ProductOptionValue = 'PRODUCT_OPTION_VALUE',
  Shop = 'SHOP',
  ShopPolicy = 'SHOP_POLICY',
  ShippingAndDelivery = 'SHIPPING_AND_DELIVERY',
  Metaobject = 'METAOBJECT',
  CookieBanner = 'COOKIE_BANNER',
}

export enum MediaUploadStatus {
  ValidationFailed,
  StagedUploadFailed,
  FinalUploadFailed,
  Success,
}

export interface FetchMoreOptions<Data, Variables = {}> {
  updateQuery(
    previousQueryResult: Data,
    options: {
      fetchMoreResult: Data;
      queryVariables: Variables;
    },
  ): Data;
}
export interface GraphQLDataProps<Data, Variables = {}> {
  error?: ApolloError;
  networkStatus: NetworkStatus;
  variables: Variables;
  fetchMore(
    fetchMoreOptions: FetchMoreQueryOptions<Variables, keyof Variables> &
      FetchMoreOptions<Data, Variables>,
  ): Promise<ApolloQueryResult<Data>>;
  refetch(variables?: Variables): Promise<ApolloQueryResult<Data>>;
  startPolling(pollInterval: number): void;
  stopPolling(): void;
  subscribeToMore(options: SubscribeToMoreOptions): () => void;
  updateQuery(
    mapFn: (
      previousQueryResult: any,
      options: UpdateQueryOptions<Variables>,
    ) => any,
  ): void;
}

export interface MutationData<Data, Variables = {}> {
  data: GraphQLResultLoadedSuccess<Data, Variables>;
}
export interface MutationOptions<Data, Variables = {}> {
  variables: Variables;
  optimisticResponse?: Data;
  update?(proxy: DataProxy, mutationData: MutationData<Data, Variables>): void;
}

export interface GraphQLMutationFunction<Data, Variables = {}> {
  (options?: MutationOptions<Data, Variables>): Promise<{data: Data}>;
}

export type GraphQLMutation<
  Data,
  Variables = {},
  MutationName extends string = 'mutate',
> = {[data in MutationName]: GraphQLMutationFunction<Data, Variables>};

export type GraphQLResultLoading<Data, Variables = {}> = {
  loading: true;
  error: undefined;
} & GraphQLDataProps<Data, Variables> & {[Name in keyof Data]: null};

export type GraphQLResultLoadedFailure<Data, Variables = {}> = {
  loading: false;
  error: ApolloError;
} & GraphQLDataProps<Data, Variables> & {[Name in keyof Data]: null};

export type GraphQLResultLoadedSuccess<Data, Variables = {}> = {
  loading: false;
  error: undefined;
} & GraphQLDataProps<Data, Variables> &
  Data;

export type GraphQLResult<Data, Variables = {}> =
  | GraphQLResultLoading<Data, Variables>
  | GraphQLResultLoadedFailure<Data, Variables>
  | GraphQLResultLoadedSuccess<Data, Variables>;

export type GraphQLQuery<
  Data,
  Variables = {},
  QueryName extends string = 'data',
  Skippable = false,
> = Skippable extends true
  ? {[data in QueryName]: GraphQLResult<Data, Variables> | undefined}
  : {[data in QueryName]: GraphQLResult<Data, Variables>};

export function dataHasErrors<Data, Variables>(
  data: GraphQLResult<Data, Variables>,
): data is GraphQLResultLoadedFailure<Data, Variables> {
  return Boolean(data.error);
}
